/**
 * Endpoints of `docs/openapi-web.yaml` the shared route table does not carry yet.
 *
 * `packages/shared/src/routes.ts` only admits routes with a mounted handler (`routeTable.test.ts`
 * enforces it), and the backend owner adds them as phases land. Until then they live here, typed
 * by hand against the YAML. When a route reaches the table, delete its entry and use `api.*`.
 */
import {
  AkpError,
  createApiClient,
  isApiErrorBody,
  type PlayerCommandNode,
  type ProductDTO,
  type QueueItemDTO,
  type SessionDTO,
  type WebSongDTO,
} from '@akpl/shared';

import { API_BASE, getAccessToken } from '../api';
import { currentLocale } from '../i18n';

export interface LibrarySummary {
  favoritesCount: number;
  historyCount: number;
}

export interface FavoriteItem {
  song: WebSongDTO;
  addedAt: string;
}

export interface FavoritesResponse {
  items: FavoriteItem[];
  total: number;
}

export interface HistoryItem {
  song: WebSongDTO;
  lastSungAt: string;
  timesSung: number;
  context: { type: 'solo' | 'party' | 'trial'; hostName?: string | null };
}

export interface HistoryResponse {
  items: HistoryItem[];
  total: number;
}

export type WebSessionMode = 'solo' | 'party';

export interface WebSessionResponse {
  session: SessionDTO;
  mode: WebSessionMode;
  customToken: string;
}

export interface PageQuery {
  offset?: number;
  limit?: number;
}

export interface EnqueueInput {
  songId: string;
  tone: string;
}

interface RequestOptions {
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

let webApiErrorHandler: ((error: AkpError) => void) | null = null;

/**
 * `webApi` and `hostApi` do not go through the `api` client of `../api`, so its error handler
 * never sees their answers. A blocked account has to end the session from any of them (§5.9).
 */
export function setWebApiErrorHandler(handler: ((error: AkpError) => void) | null): void {
  webApiErrorHandler = handler;
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${API_BASE}/v1${path}`);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  const token = await getAccessToken();
  const res = await fetch(url, {
    method,
    headers: {
      'Accept-Language': currentLocale(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  });
  if (res.status === 204) return undefined as T;
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    if (isApiErrorBody(body)) {
      const error = new AkpError(body.error.code, body.error.message, {
        status: res.status,
        details: body.error.details,
      });
      webApiErrorHandler?.(error);
      throw error;
    }
    throw new AkpError('internal', `HTTP ${res.status}`, { status: res.status });
  }
  return body as T;
}

export const webApi = {
  library: () => request<LibrarySummary>('GET', '/me/library'),
  favorites: (query: PageQuery = {}) =>
    request<FavoritesResponse>('GET', '/me/favorites', { query: { ...query } }),
  favoriteIds: () => request<{ songIds: string[] }>('GET', '/me/favorites/ids'),
  addFavorite: (songId: string) =>
    request<{ ok: true }>('PUT', `/me/favorites/${encodeURIComponent(songId)}`),
  removeFavorite: (songId: string) =>
    request<{ ok: true }>('DELETE', `/me/favorites/${encodeURIComponent(songId)}`),
  clearFavorites: () => request<{ ok: true }>('DELETE', '/me/favorites'),
  history: (query: PageQuery = {}) =>
    request<HistoryResponse>('GET', '/me/history', { query: { ...query } }),
  removeHistory: (songId: string) =>
    request<{ ok: true }>('DELETE', `/me/history/${encodeURIComponent(songId)}`),
  clearHistory: () => request<{ ok: true }>('DELETE', '/me/history'),

  createSession: (body: { deviceId: string; mode: WebSessionMode; takeover?: boolean }) =>
    request<WebSessionResponse>('POST', '/web/sessions', { body }),
  /** `null` when this device has no active session (204). */
  currentSession: (deviceId: string) =>
    request<WebSessionResponse | undefined>('GET', '/web/sessions/current', {
      query: { deviceId },
    }).then((res) => res ?? null),
  patchSession: (sid: string, mode: WebSessionMode) =>
    request<{ session: SessionDTO; mode: WebSessionMode }>('PATCH', `/web/sessions/${sid}`, {
      body: { mode },
    }),

  /** `via: 'web'` is not in the zod `EnqueueBody` until the API accepts it (docs/13 §7.2). */
  enqueue: (sid: string, input: EnqueueInput) =>
    request<{ item: QueueItemDTO }>('POST', `/sessions/${sid}/queue`, {
      body: { ...input, via: 'web' },
    }),
  playNow: (sid: string, input: EnqueueInput) =>
    request<{ item: QueueItemDTO; command: PlayerCommandNode }>(
      'POST',
      `/sessions/${sid}/play-now`,
      { body: { ...input, via: 'web' } },
    ),

  products: () => request<{ products: ProductDTO[] }>('GET', '/billing/products'),
  checkout: (body: { priceId: string; returnTo: string; embedded?: boolean }) =>
    request<{ url: string } | { clientSecret: string }>('POST', '/billing/checkout', { body }),
  cardSession: () => request<{ clientSecret: string }>('POST', '/billing/card-session'),
  portal: (body: { returnTo: string }) =>
    request<{ url: string }>('POST', '/billing/portal', { body }),
};

let hostTokenProvider: () => Promise<string | null> = async () => null;

/** Installed by the web session once the host token is signed in on the `akp-host` app. */
export function setHostTokenProvider(provider: () => Promise<string | null>): void {
  hostTokenProvider = provider;
}

/** The browser as the screen: `/songs/:id/play`, `/playback` (docs/13 §2). */
export const hostApi = createApiClient({
  baseUrl: API_BASE,
  getToken: () => hostTokenProvider(),
  headers: () => ({ 'Accept-Language': currentLocale() }),
  // Resolved per call, like `api`: tests stub the global after this module is evaluated.
  fetch: (input, init) => globalThis.fetch(input, init),
  validateResponses: import.meta.env.DEV,
  onError: (error) => webApiErrorHandler?.(error),
  timeoutMs: 15_000,
});
